import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { I18n } from 'react-redux-i18n-lite';
import React, { Component } from 'react';
import { bindActionCreators } from 'redux';

import UserImage from '../../common/UserImage';
import CustomIcon from '../../common/CustomIcon';
import NewMessageInput from './NewMessageInput';
import { ROLE } from '../../../const';
import * as discussionActions from '../../../actions/discussionActions';
import DeletedMessage from './DeletedMessage';
import { capitalize } from '../../../utils/stringUtils';

const REPLY_AVATAR_SIZES = [34, 30, 28];

class MessageItem extends Component {
  state = {
    renderMessageOptions: false,
    showReplyInput: false,
    renderEditMessageInput: false,
    isLiking: false
  };

  setIsLiking = isLiking => {
    this.setState({ isLiking });
  };

  onMouseEnter = () => {
    this.setState({ renderMessageOptions: true });
  };

  onMouseLeave = () => {
    this.setState({ renderMessageOptions: false });
  };

  toggleReplyInput = () => {
    const { showReplyInput } = this.state;
    this.setState({ showReplyInput: !showReplyInput });
  };

  createMessage = newMessageText => {
    const { discussionActions, message } = this.props;
    discussionActions.sendReplyToMessage(newMessageText, message.id);
  };

  updateMessage = messageText => {
    const { discussionActions, message } = this.props;
    discussionActions.updateDiscussionMessage(messageText, message.id);
  };

  removeMessage = () => {
    const { discussionActions, message } = this.props;
    discussionActions.removeMessage(message.id);
  };

  toggleEditMessageInput = () => {
    const { renderEditMessageInput } = this.state;
    this.setState({ renderEditMessageInput: !renderEditMessageInput });
  };

  setLikeMessage = likedByUser => {
    const { discussionActions, message } = this.props;
    const { isLiking } = this.state;

    this.setIsLiking(true);

    if (!likedByUser && !isLiking) {
      discussionActions.sendLikeToMessage(message.id);
    }
  };

  renderDeleteButton = isOwnMessage => {
    if (this.isTeacher()) {
      return (
        <div
          onClick={this.removeMessage}
          className={`clickable-action ${
            !this.state.showReplyInput && this.state.renderMessageOptions ? 'active' : ''
          }`}
        >
          {I18n.t('discussions.common.removeMessage')}
        </div>
      );
    }
  };

  renderMessageOptions = () => {
    const { user, disableReplies, message } = this.props;
    const isOwnMessage = user._id === message.author.id;

    if (disableReplies) {
      return;
    }

    return (
      <div className="reply-wrapper" onMouseOver={this.onMouseEnter} onMouseLeave={this.onMouseLeave}>
        <div
          onClick={this.toggleReplyInput}
          className={`clickable-action ${
            !this.state.showReplyInput && this.state.renderMessageOptions ? 'active' : ''
          }`}
        >
          {I18n.t('discussions.common.reply')}
        </div>
        {this.isTeacher() && isOwnMessage && (
          <div
            onClick={this.toggleEditMessageInput}
            className={`clickable-action ${
              !this.state.showReplyInput && this.state.renderMessageOptions ? 'active' : ''
            }`}
          >
            {I18n.t('discussions.common.editMessage')}
          </div>
        )}
        {this.renderDeleteButton(isOwnMessage)}
      </div>
    );
  };

  renderReplyInput = () => {
    const { disableReplies } = this.props;
    return (
      <NewMessageInput
        showCancelButton
        disabled={disableReplies}
        onCancel={this.toggleReplyInput}
        onSaveMessage={messageText => {
          this.toggleReplyInput();
          this.createMessage(messageText);
        }}
      />
    );
  };

  renderMessageReplies = () => {
    const { replies, colors, user, classroom, discussionId, discussionActions, disableReplies } = this.props;
    if (!replies || !replies.length) {
      return;
    }
    const depth = this.props.depth || 0;
    return (
      <div className="message-replies">
        {replies.map(reply =>
          reply.deleted ? (
            <DeletedMessage key={reply.id} />
          ) : (
            <MessageItem
              user={user}
              key={reply.id}
              message={reply}
              colors={colors}
              classroom={classroom}
              disableReplies={disableReplies}
              discussionActions={discussionActions}
              discussionId={discussionId}
              depth={depth + 1}
            />
          )
        )}
      </div>
    );
  };

  renderHeart = () => {
    const { message, colors, user, disableReplies } = this.props;
    const likedByUser = user && message.likes.includes(user._id);

    return (
      <CustomIcon
        onClick={() => !likedByUser && !disableReplies && this.setLikeMessage(likedByUser)}
        color={likedByUser ? colors.alert : ''}
        classes={likedByUser || disableReplies ? '' : 'clickable'}
        name={likedByUser ? 'lovers-heart-filled' : 'lovers-heart'}
        size={14}
      />
    );
  };

  renderEditMessageInput = () => {
    const { disableReplies, message } = this.props;

    return (
      <NewMessageInput
        showCancelButton
        hideUserAvatar={true}
        initialValue={message.content}
        disabled={disableReplies}
        onCancel={this.toggleEditMessageInput}
        onSaveMessage={messageText => {
          this.toggleEditMessageInput();
          this.updateMessage(messageText);
        }}
      />
    );
  };

  isUrl = word => {
    const regexUrl = /^(http(s):\/\/.)[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)$/g;
    return regexUrl.test(word);
  };

  printUrl = link => {
    return (
      <a style={{ cursor: 'pointer' }} href={link} target="_blank">
        {`${link} `}
      </a>
    );
  };

  renderMessageContentUrls = messageContent => {
    return (
      <>
        {messageContent
          .split('\n')
          .map(line => line.split(' ').map(word => (this.isUrl(word) ? this.printUrl(word) : `${word} `)))
          .map(line => (
            <div>{line}</div>
          ))}
      </>
    );
  };

  renderMessageContent = () => {
    const { message } = this.props;

    return (
      <div
        onMouseOver={this.onMouseEnter}
        onMouseLeave={this.onMouseLeave}
        className={`${this.isInstructorMessage() ? 'teacher-post' : ''} content`}
      >
        {this.getReplyMention(message)} {this.renderMessageContentUrls(message.content)}
      </div>
    );
  };

  getReplyMention = message => {
    return message.replyTo && <span className="reply-user">@{message.replyTo.author.name}</span>;
  };

  isInstructorMessage = () => this.props.message.author.role === ROLE.INSTRUCTOR;
  isTeacher = () => this.props.role === ROLE.INSTRUCTOR;

  render() {
    const { message } = this.props;
    const { showReplyInput, renderEditMessageInput } = this.state;
    const depth = this.props.depth || 0;
    const avatarSize = REPLY_AVATAR_SIZES[Math.min(depth, REPLY_AVATAR_SIZES.length - 1)];

    return (
      <div className="discussion-posts-wrapper">
        <UserImage picture={message.author.picture} classes="no-border" size={avatarSize} />
        <div className="post-content">
          <div className="post-info" onMouseOver={this.onMouseEnter} onMouseLeave={this.onMouseLeave}>
            <div className="left-side">
              <div className="user-name">{message.author.name}</div>
              <div className="date">·</div>
              <div className="date">{capitalize(dayjs(message.timestamp).fromNow())}</div>
            </div>
            <div className="right-side">
              <div className="likes">
                {message.likes.length}
                {this.renderHeart()}
              </div>
            </div>
          </div>
          {renderEditMessageInput ? this.renderEditMessageInput() : this.renderMessageContent()}
          {showReplyInput ? this.renderReplyInput() : this.renderMessageOptions()}
          {this.renderMessageReplies()}
        </div>
      </div>
    );
  }
}

MessageItem.propTypes = {
  message: PropTypes.object.isRequired,
  replies: PropTypes.array,
  user: PropTypes.object.isRequired,
  colors: PropTypes.object.isRequired,
  classroom: PropTypes.object.isRequired,
  discussionId: PropTypes.string.isRequired,
  discussionActions: PropTypes.object.isRequired,
  disableReplies: PropTypes.bool,
  role: PropTypes.string,
  depth: PropTypes.number
};

MessageItem.defaultProps = {
  depth: 0
};

function mapStateToProps(state) {
  return {
    colors: state.theme.colors,
    user: state.account.user,
    role: state.classrooms.selectedClassroom.classroom.role
  };
}

function mapDispatchToProps(dispatch) {
  return {
    discussionActions: bindActionCreators(discussionActions, dispatch)
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(MessageItem);
